// Mobile editor (domain: mobile) — self-contained, no Fabric.
//
// 1-to-1 port of the legacy mobile flow (snapshot HTML L2001-2118, JS L6883-7262):
//   STEP 1  "What are you making?" — a template grid (Address / Shipping /
//           Product / Name Badge / Return Address / Barcode / QR). Picking one
//           applies a size + seeds editable text/barcode/QR objects.
//   STEP 2  a small 2D-canvas preview + one input per editable field that
//           updates the preview live + a size picker <select> + Download PDF
//           (jsPDF) + Share / Email actions.
//
// Rendering uses a raw 2D <canvas> (not Fabric) exactly as the legacy editor
// did; barcode/QR art is produced via the existing widget helpers
// (generateBarcodeSVG / generateQRDataUrl) so widget logic is unchanged.
//
// Fixes carried over from docs/olm-extraction-map.md §5.1:
//   - the legacy dangling `mobileUpdatePreview()` (barcode/QR oninput) is wired
//     here to the real re-render function.
//   - the previously half-wired QR path now has a real QR template + live input.

import { jsPDF } from 'jspdf'
import { LABEL_SIZES, MM_TO_PX } from './constants'
import { generateBarcodeSVG } from './widgets/barcode'
import { generateQRDataUrl } from './widgets/qrcode'
import { toast } from './ui/dom'
import { ensureAdGate } from './ads/gate'
import type { LabelSize, Orientation } from './types'
import { PRODUCT, logoHtml } from './product'

const EDITOR_URL = PRODUCT.editorUrl

// ----------------------------------------------------------------------------
// Template model
// ----------------------------------------------------------------------------

interface MobileField {
  label: string
  key: string
  value: string
  fontSize: number
  fontWeight?: string
  fill?: string
  left?: number
  top?: number
  /** vertical position as a fraction of label height (overrides `top`) */
  topPct?: number
  centerX?: boolean
  rightAlign?: boolean
}

type Extra =
  | { type: 'rect'; left: number; top: number; width: number; height: number; fill?: string }
  | { type: 'line'; x1: number; y1: number; x2: number; y2: number; stroke?: string; strokeWidth?: number }
  | { type: 'text'; text: string; left: number; top: number; fontSize?: number; fontWeight?: string; fill?: string }

interface MobileTemplate {
  /** preferred size id; falls back to the first LABEL_SIZES match if absent */
  size: string
  orientation: Orientation
  fields: MobileField[]
  extras?: (w: number, h: number) => Extra[]
  barcode?: { format: string; value: string }
  qr?: { value: string }
}

// Template definitions — text fields are what the user can edit. Sizes map to
// the v2 LABEL_SIZES ids (legacy ids like dymo-99012 are not in this subset, so
// the nearest real size is used).
const MOBILE_TEMPLATES: Record<string, MobileTemplate> = {
  address: {
    size: 'dymo-30252', orientation: 'landscape',
    fields: [
      { label: 'Full name', key: 'name', value: 'John Smith', fontSize: 18, fontWeight: 'bold', fill: '#0f172a', left: 12, top: 10 },
      { label: 'Address line 1', key: 'addr1', value: '123 Main Street, Apt 4B', fontSize: 13, fill: '#334155', left: 12, top: 35 },
      { label: 'City, state, zip', key: 'addr2', value: 'New York, NY 10001', fontSize: 13, fill: '#334155', left: 12, top: 55 },
      { label: 'Country', key: 'country', value: 'United States', fontSize: 13, fill: '#334155', left: 12, top: 75 },
    ],
  },
  shipping: {
    size: 'zebra-102x152', orientation: 'portrait',
    fields: [
      { label: 'Recipient name', key: 'name', value: 'Jane Doe', fontSize: 22, fontWeight: 'bold', fill: '#0f172a', left: 20, topPct: 0.12 },
      { label: 'Address line 1', key: 'addr1', value: '456 Oak Avenue, Suite 200', fontSize: 16, fill: '#334155', left: 20, topPct: 0.18 },
      { label: 'City, state, zip', key: 'addr2', value: 'Los Angeles, CA 90001', fontSize: 16, fill: '#334155', left: 20, topPct: 0.23 },
      { label: 'Sender info', key: 'from', value: 'Your Company · 789 Elm St · Chicago, IL', fontSize: 11, fill: '#64748b', left: 10, topPct: 0.49 },
    ],
    extras: (w, h) => [
      { type: 'rect', left: 0, top: 0, width: w, height: h * 0.08, fill: '#0f172a' },
      { type: 'text', text: 'SHIP TO', left: 10, top: h * 0.015, fontSize: 14, fontWeight: 'bold', fill: '#ffffff' },
      { type: 'line', x1: 10, y1: h * 0.42, x2: w - 10, y2: h * 0.42, stroke: '#cbd5e1' },
      { type: 'text', text: 'FROM', left: 10, top: h * 0.45, fontSize: 10, fontWeight: 'bold', fill: '#94a3b8' },
    ],
  },
  product: {
    size: 'dymo-30336', orientation: 'landscape',
    fields: [
      { label: 'Product name', key: 'name', value: 'PRODUCT NAME', fontSize: 11, fontWeight: 'bold', fill: '#0f172a', left: 8, top: 6 },
      { label: 'SKU / code', key: 'sku', value: 'SKU: ABC-12345', fontSize: 9, fill: '#64748b', left: 8, top: 22 },
      { label: 'Price', key: 'price', value: '$24.99', fontSize: 14, fontWeight: 'bold', fill: '#0f172a', rightAlign: true, top: 6 },
    ],
    barcode: { format: 'CODE128', value: '12345678' },
  },
  name: {
    size: 'dymo-30321', orientation: 'landscape',
    fields: [
      { label: 'Your name', key: 'name', value: 'Your Name', fontSize: 28, fontWeight: 'bold', fill: '#0f172a', centerX: true, top: 52 },
    ],
    extras: (w) => [
      { type: 'text', text: 'HELLO', left: w / 2 - 30, top: 10, fontSize: 12, fontWeight: 'bold', fill: '#2563eb' },
      { type: 'text', text: 'my name is', left: w / 2 - 35, top: 28, fontSize: 10, fill: '#64748b' },
      { type: 'line', x1: 20, y1: 45, x2: w - 20, y2: 45, stroke: '#e2e8f0' },
    ],
  },
  return: {
    size: 'dymo-30330', orientation: 'landscape',
    fields: [
      { label: 'Name', key: 'name', value: 'Your Name', fontSize: 14, fontWeight: 'bold', fill: '#0f172a', left: 10, top: 8 },
      { label: 'Street address', key: 'street', value: '123 Your Street', fontSize: 11, fill: '#334155', left: 10, top: 28 },
      { label: 'City, state, zip', key: 'city', value: 'City, State 00000', fontSize: 11, fill: '#334155', left: 10, top: 44 },
    ],
  },
  barcode: {
    size: 'dymo-30252', orientation: 'landscape',
    fields: [
      { label: 'Description', key: 'desc', value: 'Item Description', fontSize: 13, fontWeight: 'bold', fill: '#0f172a', left: 10, top: 8 },
    ],
    barcode: { format: 'CODE128', value: '12345678' },
  },
  qr: {
    size: 'dymo-30332', orientation: 'landscape',
    fields: [
      { label: 'Caption', key: 'caption', value: 'Scan me', fontSize: 11, fontWeight: 'bold', fill: '#0f172a', centerX: true, top: 4 },
    ],
    qr: { value: PRODUCT.qrPlaceholder },
  },
}

interface TemplateMeta {
  key: string
  title: string
  subtitle: string
  iconSvg: string
}

// Step-1 template cards (label outline glyphs, copied from snapshot L2024-2053).
const TEMPLATE_CARDS: TemplateMeta[] = [
  { key: 'address', title: 'Address Label', subtitle: 'DYMO 99012', iconSvg: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M7 9h6M7 12h10M7 15h4"/>' },
  { key: 'shipping', title: 'Shipping Label', subtitle: '4×6 thermal', iconSvg: '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="M6 8h5M6 11h12M6 14h8M6 17h4"/><circle cx="18" cy="16" r="2"/>' },
  { key: 'product', title: 'Product Label', subtitle: 'With barcode', iconSvg: '<rect x="3" y="6" width="18" height="12" rx="2"/><path d="M7 10h4M7 13h6"/><rect x="14" y="9" width="4" height="6" rx="0.5" stroke-width="1"/>' },
  { key: 'name', title: 'Name Badge', subtitle: 'DYMO 11356', iconSvg: '<rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="10" r="2.5"/><path d="M5 17c0-2 1.8-3 4-3s4 1 4 3M15 9h4M15 12h3"/>' },
  { key: 'return', title: 'Return Address', subtitle: 'DYMO 99010', iconSvg: '<rect x="4" y="7" width="16" height="10" rx="2"/><path d="M8 11h5M8 14h3"/>' },
  { key: 'barcode', title: 'Barcode Label', subtitle: 'Code 128', iconSvg: '<rect x="3" y="6" width="18" height="12" rx="2"/><path d="M7 9v6M9 9v6M11 9v4M13 9v6M15 9v3M17 9v6" stroke-width="1"/>' },
  { key: 'qr', title: 'QR Code Label', subtitle: 'Scan to link', iconSvg: '<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="3" height="3"/><rect x="18" y="18" width="3" height="3"/>' },
]

// ----------------------------------------------------------------------------
// Module state
// ----------------------------------------------------------------------------

let host: HTMLElement | null = null
let currentTemplate: string | null = null
let textFields: MobileField[] = []
let barcodeValue = ''
let qrValue = ''
let qrDataUrlCache = ''

// Live DOM refs (rebuilt per step)
let step1El: HTMLElement
let step2El: HTMLElement
let fieldsEl: HTMLElement
let previewWrap: HTMLDivElement
let previewCanvas: HTMLCanvasElement
let sizeSelect: HTMLSelectElement
let barcodeFieldEl: HTMLElement
let barcodeInput: HTMLInputElement
let qrFieldEl: HTMLElement
let qrInput: HTMLInputElement

// ----------------------------------------------------------------------------
// Detection
// ----------------------------------------------------------------------------

/** Legacy IS_MOBILE: mobile UA OR a narrow (<768px) viewport. */
export function isMobile(): boolean {
  return (
    /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    window.innerWidth < 768
  )
}

// ----------------------------------------------------------------------------
// Geometry helpers
// ----------------------------------------------------------------------------

function selectedSize(fallbackId: string): LabelSize {
  const id = sizeSelect?.value || fallbackId
  return LABEL_SIZES.find((s) => s.id === id) ?? LABEL_SIZES.find((s) => s.id === fallbackId) ?? LABEL_SIZES[0]
}

function labelPx(size: LabelSize, orient: Orientation): { w: number; h: number; mmW: number; mmH: number } {
  const mmW = orient === 'landscape' ? Math.max(size.widthMM, size.heightMM) : Math.min(size.widthMM, size.heightMM)
  const mmH = orient === 'landscape' ? Math.min(size.widthMM, size.heightMM) : Math.max(size.widthMM, size.heightMM)
  return { w: Math.round(mmW * MM_TO_PX), h: Math.round(mmH * MM_TO_PX), mmW, mmH }
}

// ----------------------------------------------------------------------------
// 2D rendering (preview + export share the same draw routine)
// ----------------------------------------------------------------------------

function drawExtras(ctx: CanvasRenderingContext2D, tmpl: MobileTemplate, w: number, h: number): void {
  if (!tmpl.extras) return
  for (const e of tmpl.extras(w, h)) {
    if (e.type === 'rect') {
      ctx.fillStyle = e.fill || '#000'
      ctx.fillRect(e.left, e.top, e.width, e.height)
    } else if (e.type === 'line') {
      ctx.strokeStyle = e.stroke || '#000'
      ctx.lineWidth = e.strokeWidth || 1
      ctx.beginPath()
      ctx.moveTo(e.x1, e.y1)
      ctx.lineTo(e.x2, e.y2)
      ctx.stroke()
    } else {
      ctx.fillStyle = e.fill || '#000'
      ctx.font = `${e.fontWeight || 'normal'} ${e.fontSize || 12}px Inter, sans-serif`
      ctx.fillText(e.text, e.left, e.top + (e.fontSize || 12))
    }
  }
}

function drawFields(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  for (const f of textFields) {
    const y = f.topPct != null ? h * f.topPct : (f.top ?? 10)
    ctx.fillStyle = f.fill || '#000'
    ctx.font = `${f.fontWeight || 'normal'} ${f.fontSize}px Inter, sans-serif`
    if (f.centerX) {
      const tw = ctx.measureText(f.value).width
      ctx.fillText(f.value, w / 2 - tw / 2, y + f.fontSize)
    } else if (f.rightAlign) {
      const tw = ctx.measureText(f.value).width
      ctx.fillText(f.value, w - tw - 10, y + f.fontSize)
    } else {
      ctx.fillText(f.value, f.left ?? 10, y + f.fontSize)
    }
  }
}

function svgToImage(svg: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)))
  })
}

function urlToImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

/** Draw a label onto `ctx` at logical size w×h (background + extras + fields + barcode/QR). */
async function drawLabel(ctx: CanvasRenderingContext2D, tmpl: MobileTemplate, w: number, h: number): Promise<void> {
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, w, h)
  drawExtras(ctx, tmpl, w, h)
  drawFields(ctx, w, h)

  if (tmpl.barcode) {
    try {
      const svg = generateBarcodeSVG(tmpl.barcode.format, barcodeValue || tmpl.barcode.value, { fontSize: 12 })
      const bImg = await svgToImage(svg)
      const bScale = Math.min((w * 0.85) / bImg.width, 1)
      ctx.drawImage(bImg, w / 2 - (bImg.width * bScale) / 2, h * 0.45, bImg.width * bScale, bImg.height * bScale)
    } catch { /* ignore barcode render failure */ }
  }

  if (tmpl.qr) {
    try {
      const dataUrl = qrDataUrlCache || (await generateQRDataUrl(qrValue || tmpl.qr.value))
      const qImg = await urlToImage(dataUrl)
      const qSide = Math.min(h * 0.7, w * 0.7)
      ctx.drawImage(qImg, w / 2 - qSide / 2, h * 0.25, qSide, qSide)
    } catch { /* ignore qr render failure */ }
  }
}

let renderToken = 0
async function renderPreview(): Promise<void> {
  const tmpl = currentTemplate ? MOBILE_TEMPLATES[currentTemplate] : null
  if (!tmpl) return
  const size = selectedSize(tmpl.size)
  const orient = tmpl.orientation
  const { w: pxW, h: pxH } = labelPx(size, orient)

  const maxWidth = Math.min(320, window.innerWidth - 40)
  const scale = maxWidth / pxW

  previewWrap.style.width = maxWidth + 'px'
  previewWrap.style.height = Math.round(pxH * scale) + 'px'
  previewCanvas.width = pxW
  previewCanvas.height = pxH
  previewCanvas.style.width = maxWidth + 'px'
  previewCanvas.style.height = Math.round(pxH * scale) + 'px'

  const ctx = previewCanvas.getContext('2d')
  if (!ctx) return

  // Pre-resolve QR once for live edits (async lib) so subsequent draws are sync.
  if (tmpl.qr) {
    try { qrDataUrlCache = await generateQRDataUrl(qrValue || tmpl.qr.value) } catch { qrDataUrlCache = '' }
  }

  const token = ++renderToken
  await drawLabel(ctx, tmpl, pxW, pxH)
  // If a newer render started while awaiting, redo to avoid a stale frame.
  if (token !== renderToken) await renderPreview()
}

// ----------------------------------------------------------------------------
// Step transitions + field wiring
// ----------------------------------------------------------------------------

function loadTemplate(key: string): void {
  const tmpl = MOBILE_TEMPLATES[key]
  if (!tmpl) return
  currentTemplate = key
  textFields = tmpl.fields.map((f) => ({ ...f }))
  barcodeValue = tmpl.barcode?.value ?? ''
  qrValue = tmpl.qr?.value ?? ''
  qrDataUrlCache = ''

  sizeSelect.value = tmpl.size

  step1El.classList.add('hidden')
  step2El.classList.remove('hidden')
  setBackVisible(true)

  // barcode / QR field visibility
  barcodeFieldEl.classList.toggle('hidden', !tmpl.barcode)
  qrFieldEl.classList.toggle('hidden', !tmpl.qr)
  if (tmpl.barcode) barcodeInput.value = tmpl.barcode.value
  if (tmpl.qr) qrInput.value = tmpl.qr.value

  // One <input> per editable text field.
  fieldsEl.innerHTML = ''
  textFields.forEach((f, i) => {
    const wrap = document.createElement('div')
    wrap.className = 'mobile-field'
    const label = document.createElement('label')
    label.textContent = f.label
    const input = document.createElement('input')
    input.type = 'text'
    input.value = f.value
    input.autocomplete = 'off'
    input.className = 'w-full h-10 rounded-lg border border-slate-700 bg-slate-800 text-white text-sm px-3'
    input.addEventListener('input', () => {
      textFields[i].value = input.value
      void renderPreview()
    })
    wrap.append(label, input)
    fieldsEl.append(wrap)
  })

  void renderPreview()
}

let backBtnEl: HTMLButtonElement | null = null

/** The header "<" only makes sense on the design step (customer: "does nothing"). */
function setBackVisible(visible: boolean): void {
  if (backBtnEl) backBtnEl.classList.toggle('hidden', !visible)
}

function back(): void {
  step1El.classList.remove('hidden')
  step2El.classList.add('hidden')
  currentTemplate = null
  setBackVisible(false)
}

// ----------------------------------------------------------------------------
// Export / share
// ----------------------------------------------------------------------------

async function exportLabel(): Promise<void> {
  if (!currentTemplate) { toast('Pick a template first'); return }
  if (await ensureAdGate('mobile-export') === 'deny') return
  const tmpl = MOBILE_TEMPLATES[currentTemplate]
  const size = selectedSize(tmpl.size)
  const { w: pxW, h: pxH, mmW, mmH } = labelPx(size, tmpl.orientation)

  // Render at 3× into an offscreen canvas.
  const cvs = document.createElement('canvas')
  cvs.width = pxW * 3
  cvs.height = pxH * 3
  const ctx = cvs.getContext('2d')
  if (!ctx) return
  ctx.scale(3, 3)

  if (tmpl.qr) {
    try { qrDataUrlCache = await generateQRDataUrl(qrValue || tmpl.qr.value) } catch { qrDataUrlCache = '' }
  }
  await drawLabel(ctx, tmpl, pxW, pxH)

  const dataUrl = cvs.toDataURL('image/png')
  const pdf = new jsPDF({ orientation: mmW > mmH ? 'landscape' : 'portrait', unit: 'mm', format: [mmW, mmH] })
  pdf.addImage(dataUrl, 'PNG', 0, 0, mmW, mmH, undefined, 'FAST')
  const name = (textFields[0]?.value || 'label')
    .replace(/[^a-zA-Z0-9\s\-_.]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase()
  await deliverPdf(pdf, `${name || 'label'}.pdf`)
}

/**
 * Hand the PDF to the user. iOS Safari is unreliable with programmatic
 * <a download> (jsPDF's save): prefer the native share sheet with the file
 * (Files / AirDrop / Print / Mail…) when the browser can share files; fall
 * back to a normal download elsewhere. A cancelled share sheet is not an error.
 */
async function deliverPdf(pdf: jsPDF, filename: string): Promise<void> {
  try {
    const blob = pdf.output('blob')
    const file = new File([blob], filename, { type: 'application/pdf' })
    const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean }
    if (typeof nav.share === 'function' && nav.canShare?.({ files: [file] })) {
      try {
        await nav.share({ files: [file], title: filename })
        toast('PDF ready ✓')
        return
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return // user closed the share sheet
        // any other failure → fall through to a plain download
      }
    }
  } catch { /* File/blob unsupported → plain download */ }
  pdf.save(filename)
  toast('PDF exported ✓')
}

async function share(): Promise<void> {
  if (navigator.share) {
    try {
      await navigator.share({
        title: PRODUCT.mobileShare.title,
        text: PRODUCT.mobileShare.text,
        url: EDITOR_URL,
      })
    } catch { /* user cancelled */ }
  } else {
    emailLink()
  }
}

function emailLink(): void {
  const subject = encodeURIComponent(PRODUCT.mobileShare.emailSubject)
  const body = encodeURIComponent(
    'Open this link on your computer to use the full label editor:\n\n' + EDITOR_URL + '\n\nFree, no account needed.',
  )
  window.location.href = `mailto:?subject=${subject}&body=${body}`
}

function copyLink(): void {
  navigator.clipboard
    .writeText(EDITOR_URL)
    .then(() => toast('Link copied ✓'))
    .catch(() => {
      const inp = document.createElement('input')
      inp.value = EDITOR_URL
      document.body.appendChild(inp)
      inp.select()
      try { document.execCommand('copy') } catch { /* ignore */ }
      document.body.removeChild(inp)
      toast('Link copied ✓')
    })
}

// ----------------------------------------------------------------------------
// DOM construction (snapshot HTML L2001-2118)
// ----------------------------------------------------------------------------

function buildDom(): HTMLElement {
  const root = document.createElement('div')
  root.id = 'mobileEditor'
  root.className = 'fixed inset-0 z-[250] bg-slate-950 flex flex-col'

  // --- top bar ---
  const top = document.createElement('div')
  top.className = 'flex items-center h-12 bg-slate-900 border-b border-slate-800 px-4 shrink-0'
  const backBtn = document.createElement('button')
  backBtn.className = 'text-slate-400 mr-3'
  backBtn.innerHTML = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>'
  backBtn.addEventListener('click', back)
  backBtn.classList.add('hidden') // shown once a template is picked (step 2)
  backBtnEl = backBtn
  const brand = document.createElement('div')
  brand.className = 'flex items-center gap-2'
  brand.innerHTML =
    '<svg viewBox="0 0 32 32" class="w-6 h-6" fill="none"><rect width="32" height="32" rx="8" fill="#2563eb"/><path d="M22 8H10a1 1 0 0 0-1 1v14.5l6.5-3 6.5 3V9a1 1 0 0 0-1-1z" fill="white"/></svg>' +
    `<span class="text-sm font-bold text-white">${logoHtml('text-blue-500')}</span>`
  const spacer = document.createElement('div')
  spacer.className = 'flex-1'
  const exportTop = document.createElement('button')
  exportTop.className = 'text-xs font-semibold text-blue-400 px-3 py-1.5 rounded-lg border border-blue-500/30'
  exportTop.textContent = 'Export PDF'
  exportTop.addEventListener('click', () => void exportLabel())
  top.append(backBtn, brand, spacer, exportTop)

  // --- step 1: template grid ---
  step1El = document.createElement('div')
  step1El.id = 'mobileStep1'
  step1El.className = 'flex-1 overflow-y-auto p-4'

  const h2 = document.createElement('h2')
  h2.className = 'text-lg font-bold text-white text-center mb-1'
  h2.textContent = 'What are you making?'
  const sub = document.createElement('p')
  sub.className = 'text-xs text-slate-500 text-center mb-5'
  sub.textContent = 'Pick a template, edit the text, export as PDF'

  const grid = document.createElement('div')
  grid.className = 'grid grid-cols-2 gap-3 mb-6'
  for (const card of TEMPLATE_CARDS) {
    const btn = document.createElement('button')
    btn.className = 'start-template-btn'
    btn.innerHTML =
      `<svg class="w-6 h-6 mb-1 text-blue-400 mx-auto" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">${card.iconSvg}</svg>` +
      `<div class="text-sm font-semibold text-white">${card.title}</div>` +
      `<div class="text-[10px] text-slate-400">${card.subtitle}</div>`
    btn.addEventListener('click', () => loadTemplate(card.key))
    grid.append(btn)
  }

  // --- "need full editor?" note ---
  const note = document.createElement('div')
  note.className = 'bg-slate-900 rounded-xl border border-slate-800 p-5 text-center mb-6'
  const noteTitle = document.createElement('div')
  noteTitle.className = 'text-sm font-semibold text-white mb-2'
  noteTitle.textContent = 'Need the full editor?'
  const noteP = document.createElement('p')
  noteP.className = 'text-xs text-slate-400 mb-4'
  noteP.textContent = 'The full drag-and-drop editor with shapes, images, fonts, and more works best on a computer.'
  const noteActions = document.createElement('div')
  noteActions.className = 'flex flex-col gap-2'
  const emailBtn = document.createElement('button')
  emailBtn.className = 'w-full py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-sm text-white font-medium'
  emailBtn.textContent = '✉️ Email myself the link'
  emailBtn.addEventListener('click', emailLink)
  const copyBtn = document.createElement('button')
  copyBtn.className = 'w-full py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-sm text-white font-medium'
  copyBtn.textContent = '📋 Copy link'
  copyBtn.addEventListener('click', copyLink)
  noteActions.append(emailBtn, copyBtn)
  note.append(noteTitle, noteP, noteActions)

  const privacy = document.createElement('div')
  privacy.className = 'text-center text-[11px] text-slate-600'
  privacy.innerHTML =
    '<svg class="w-3.5 h-3.5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>' +
    'Your data stays private — sign up to save to the cloud'

  step1El.append(h2, sub, grid, note, privacy)

  // --- step 2: edit fields + preview ---
  step2El = document.createElement('div')
  step2El.id = 'mobileStep2'
  step2El.className = 'flex-1 overflow-y-auto hidden'

  const previewBox = document.createElement('div')
  previewBox.className = 'bg-slate-900 p-4 border-b border-slate-800'
  const previewLabel = document.createElement('div')
  previewLabel.className = 'text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2 text-center'
  previewLabel.textContent = 'Preview'
  previewWrap = document.createElement('div')
  previewWrap.id = 'mobilePreview'
  previewWrap.className = 'bg-white rounded-lg mx-auto shadow-xl overflow-hidden'
  previewWrap.style.maxWidth = '320px'
  previewCanvas = document.createElement('canvas')
  previewCanvas.id = 'mobilePreviewCanvas'
  previewWrap.append(previewCanvas)
  previewBox.append(previewLabel, previewWrap)

  fieldsEl = document.createElement('div')
  fieldsEl.id = 'mobileFields'
  fieldsEl.className = 'p-4 space-y-3'

  // size picker
  const sizeBox = document.createElement('div')
  sizeBox.className = 'px-4 pb-2'
  const sizeLabel = document.createElement('div')
  sizeLabel.className = 'text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2'
  sizeLabel.textContent = 'Label size'
  sizeSelect = document.createElement('select')
  sizeSelect.id = 'mobileSizePicker'
  sizeSelect.className = 'w-full h-10 rounded-lg border border-slate-700 bg-slate-800 text-white text-sm px-3'
  for (const s of LABEL_SIZES) {
    const opt = document.createElement('option')
    opt.value = s.id
    opt.textContent = `${s.brand ?? ''} ${s.name} — ${s.widthMM}×${s.heightMM}mm`.trim()
    sizeSelect.append(opt)
  }
  sizeSelect.addEventListener('change', () => void renderPreview())
  sizeBox.append(sizeLabel, sizeSelect)

  // barcode value (fixes legacy dangling mobileUpdatePreview)
  barcodeFieldEl = document.createElement('div')
  barcodeFieldEl.id = 'mobileBarcodeField'
  barcodeFieldEl.className = 'px-4 pb-2 hidden'
  const barcodeLbl = document.createElement('div')
  barcodeLbl.className = 'text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2'
  barcodeLbl.textContent = 'Barcode value'
  barcodeInput = document.createElement('input')
  barcodeInput.type = 'text'
  barcodeInput.id = 'mobileBarcodeValue'
  barcodeInput.className = 'w-full h-10 rounded-lg border border-slate-700 bg-slate-800 text-white text-sm px-3'
  barcodeInput.placeholder = 'Enter barcode value...'
  barcodeInput.addEventListener('input', () => { barcodeValue = barcodeInput.value; void renderPreview() })
  barcodeFieldEl.append(barcodeLbl, barcodeInput)

  // QR value (fixes legacy half-wired QR path)
  qrFieldEl = document.createElement('div')
  qrFieldEl.id = 'mobileQRField'
  qrFieldEl.className = 'px-4 pb-2 hidden'
  const qrLbl = document.createElement('div')
  qrLbl.className = 'text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2'
  qrLbl.textContent = 'QR code URL'
  qrInput = document.createElement('input')
  qrInput.type = 'url'
  qrInput.id = 'mobileQRValue'
  qrInput.className = 'w-full h-10 rounded-lg border border-slate-700 bg-slate-800 text-white text-sm px-3'
  qrInput.placeholder = 'https://...'
  qrInput.addEventListener('input', () => { qrValue = qrInput.value; qrDataUrlCache = ''; void renderPreview() })
  qrFieldEl.append(qrLbl, qrInput)

  // actions
  const actions = document.createElement('div')
  actions.className = 'p-4 space-y-2'
  const downloadBtn = document.createElement('button')
  downloadBtn.className = 'w-full py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm'
  downloadBtn.textContent = 'Download PDF'
  downloadBtn.addEventListener('click', () => void exportLabel())
  const shareBtn = document.createElement('button')
  shareBtn.className = 'w-full py-3 rounded-xl bg-slate-800 text-slate-300 font-medium text-sm border border-slate-700'
  shareBtn.textContent = 'Share / Email Label'
  shareBtn.addEventListener('click', () => void share())
  actions.append(downloadBtn, shareBtn)

  step2El.append(previewBox, fieldsEl, sizeBox, barcodeFieldEl, qrFieldEl, actions)

  root.append(top, step1El, step2El)
  return root
}

// ----------------------------------------------------------------------------
// Entry point
// ----------------------------------------------------------------------------

/**
 * Mount + show the mobile editor (replacing the desktop app/start screen).
 * Called by the boot/toolbar path when isMobile() is true.
 */
export function openMobileEditor(): void {
  if (host) {
    host.style.display = 'flex'
    return
  }
  // Hide the desktop app + start screen if present.
  const app = document.getElementById('app')
  if (app) app.style.display = 'none'
  const start = document.getElementById('startScreen')
  if (start) start.style.display = 'none'

  host = buildDom()
  document.body.append(host)
}
